// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import clsx from 'clsx'
import styles from './DeclaredFingerprint.module.css'
import type { DeclaredProduct } from 'lib/owner-notes/declared'
import { axisScore, hoursText, labTested } from 'lib/owner-notes/deltas'
import { YOURS_COLOR } from 'lib/owner-notes/curves'

/**
 * The declared pair's own numbers in orange, one line — the legend every card's "versus
 * yours" block reads against, stated once on the toolbar chip instead of on each card
 * (proposal C, 2026-09-08; the earlier band row also drew the frequency curve — the cards'
 * sparklines carry it now). "Not lab-tested" when the pair was never measured — the one
 * place that says why the cards fall to battery and specs (no per-card disclaimer).
 */
const DeclaredFingerprint: React.VFC<{
  declared: DeclaredProduct
  /** Each number in its own white pill — the guide's reference strip (design 2026-09-17).
   *  The numbers stay orange: the reader's colour on every surface (Constraint `f80d2e70`). */
  pills?: boolean
}> = ({ declared, pills = false }) => {
  const scores = [axisScore(declared.axes, 'sound'), axisScore(declared.axes, 'anc')]
  const tested = labTested(declared)

  return (
    <div className={clsx(styles.chip, pills && styles.pills)}>
      {scores.map(
        score =>
          score && (
            <span key={score.label} className={styles.stat}>
              <span className={styles.k}>{score.label}</span>
              <span className={styles.v} style={{ color: YOURS_COLOR }}>
                {score.value.toFixed(1)}
              </span>
              <span className={styles.scale}>{`/${score.max}`}</span>
            </span>
          )
      )}
      {declared.batteryHours !== null && (
        <span className={styles.stat}>
          <span className={styles.k}>{'Battery'}</span>
          <span className={styles.v} style={{ color: YOURS_COLOR }}>
            {hoursText(declared.batteryHours)}
          </span>
        </span>
      )}
      {!tested && <span className={styles.text}>{'not lab-tested'}</span>}
    </div>
  )
}

export default DeclaredFingerprint
